/**
 * HierarchyExplainerButton - Info button + modal explaining the Deliverable Rule.
 *
 * Shows when tasks should be parents vs subtasks, with concrete examples.
 * Designed to educate users on ClickUp task hierarchy best practices.
 *
 * @module HierarchyExplainerModal
 */

import { useState } from 'react';
import { CircleHelp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SimpleDialog } from '@/components/shared/DialogWrapper';

/**
 * Small info button that opens a modal explaining parent vs subtask classification.
 *
 * @returns A ghost button with CircleHelp icon + dialog
 */
export function HierarchyExplainerButton() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground"
        onClick={() => setOpen(true)}
        aria-label="Task hierarchy guide"
      >
        <CircleHelp className="h-3.5 w-3.5" />
      </Button>

      <SimpleDialog
        open={open}
        onOpenChange={setOpen}
        title="The Deliverable Rule"
        description="How Poco decides whether new work is a task or a subtask"
      >
        <div className="space-y-4 text-sm">
          <p className="text-muted-foreground">
            Poco uses the <strong>Deliverable Rule</strong> to consistently classify work items.
            This helps keep your ClickUp organized and prevents duplicate or misplaced tasks.
          </p>

          <div className="rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="p-2 text-left font-medium">Signal</th>
                  <th className="p-2 text-left font-medium">Parent Task</th>
                  <th className="p-2 text-left font-medium">Subtask</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-2 text-muted-foreground">Scope</td>
                  <td className="p-2">Delivered independently</td>
                  <td className="p-2">Only makes sense under a parent</td>
                </tr>
                <tr className="border-b">
                  <td className="p-2 text-muted-foreground">Effort</td>
                  <td className="p-2">Multiple sessions / people</td>
                  <td className="p-2">Single person, single session (&lt;4h)</td>
                </tr>
                <tr className="border-b">
                  <td className="p-2 text-muted-foreground">Deadline</td>
                  <td className="p-2">Externally visible</td>
                  <td className="p-2">Internal only</td>
                </tr>
                <tr>
                  <td className="p-2 text-muted-foreground">Client visibility</td>
                  <td className="p-2">&quot;Monthly performance deck&quot;</td>
                  <td className="p-2">&quot;Format charts in brand colors&quot;</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="rounded-md bg-muted/50 p-3 space-y-1.5">
            <p className="font-medium text-xs uppercase tracking-wide">Tips</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground text-xs">
              <li>If work could live in a different ClickUp list, it&apos;s a separate task</li>
              <li>If a parent would only have 1 subtask, just make it a regular task</li>
              <li>Never nest subtasks under other subtasks — keep it one level deep</li>
            </ul>
          </div>
        </div>
      </SimpleDialog>
    </>
  );
}
